import * as THREE from 'three'
import type { SpectrumStyle, VisualParams } from '../../types'
import type { SharedUniforms } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, ZONE_GLSL_COMMON, ZONE_GLSL_FRAG } from '../shaderChunks'

/**
 * Frequency spectrum: a set of 3D bars, each one's height driven by its
 * frequency magnitude (sampled from the spectrum texture) — an equalizer made
 * of geometry. It IS the spectrum, so the reaction is direct and obvious. Hue
 * runs along the bars; the beat lifts them a touch.
 *
 * The layout is selectable (`params.spectrumStyle`):
 * - `linear`: a straight, upright row facing the camera (classic equalizer)
 * - `mirror`: a straight row mirrored above and below a center baseline
 * - `arc`: a 180° curved wall of upright bars facing the camera
 * - `radial`: a full 360° ring (circular equalizer)
 *
 * Every layout shares one shader: each vertex carries a footprint position
 * (`position`), an extrude direction (`aUp`) and a fraction along the bar
 * (`aFrac`, 0 at the base, 1 at the tip). The shader extrudes the bar by
 * `aUp * aFrac * height`, so only the per-bar footprint and `aUp` differ
 * between styles.
 */
export class Spectrum extends BasePrimitive {
  object3D: THREE.Mesh
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: false,
      depthWrite: true,
      vertexShader: /* glsl */ `
        attribute float aBar;        // 0..1 across the spectrum -> frequency coord
        attribute vec3 aUp;          // extrude direction (and length) of the bar
        attribute float aFrac;       // 0 at the base, 1 at the tip
        uniform sampler2D uFreq;
        uniform float uReactivity, uBeat;
        varying float vMag;
        varying float vFrac;
        varying float vBar;
        varying vec3 vDir;
        ${ZONE_GLSL_COMMON}
        void main() {
          float mag = texture2D(uFreq, vec2(aBar, 0.5)).r;
          vMag = mag;
          vBar = aBar;
          vFrac = aFrac;
          // When zone reaction is on, the spectrum response is frozen — only the
          // bars under a zone grow on its band's hit.
          float gate = 1.0 - uZoneEnabled;
          float height = 0.25 + (mag * 5.5 * (0.6 + 0.4 * uReactivity) + uBeat * 0.3 * uReactivity) * gate;
          // Bars are laid along the zone sphere's equator by frequency (aBar -> longitude),
          // so a zone swept across the equator lifts the bars beneath it.
          vec3 zdir = zoneDirFromUV(vec2(aBar, 0.5));
          vDir = zdir;
          height += zoneDisp(zdir) * 3.0;
          vec3 pos = position + aUp * (aFrac * height);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform float uHue;
        varying float vMag;
        varying float vFrac;
        varying float vBar;
        varying vec3 vDir;
        ${hsv2rgb}
        ${ZONE_GLSL_COMMON}
        ${ZONE_GLSL_FRAG}
        void main() {
          float hue = fract(uHue + vBar * 0.6);
          vec3 col = hsv2rgb(vec3(hue, 0.8, 0.35 + vMag * 0.8));
          col *= 0.55 + vFrac * 0.6;     // brighter toward the tip
          // Zone reaction: bars under a zone take its color.
          vec4 zc = zoneColor(vDir);
          col = mix(col, zc.rgb, zc.a);
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    })
    this.object3D = new THREE.Mesh(new THREE.BufferGeometry(), this.material)
    this.object3D.frustumCulled = false
    this.rebuild({ density: 1, spectrumStyle: 'linear' } as VisualParams)
  }

  rebuild(params: VisualParams): void {
    const style: SpectrumStyle = params.spectrumStyle ?? 'linear'
    const bars = Math.max(32, Math.min(160, Math.floor(64 * (params.density ?? 1))))
    // `mirror` builds two bars per frequency (one up, one down).
    const boxesPerBar = style === 'mirror' ? 2 : 1
    const nBoxes = bars * boxesPerBar
    const positions = new Float32Array(nBoxes * 8 * 3)
    const aUp = new Float32Array(nBoxes * 8 * 3)
    const aFrac = new Float32Array(nBoxes * 8)
    const aBar = new Float32Array(nBoxes * 8)
    const indices = new Uint32Array(nBoxes * 36)
    let v = 0
    let ii = 0

    // Push one extruded bar: a footprint centered at `c` with half-extents `w`
    // (along `dirA`) and `d` (along `dirB`), extruded along `up`.
    const pushBox = (
      c: THREE.Vector3,
      dirA: THREE.Vector3,
      dirB: THREE.Vector3,
      up: THREE.Vector3,
      w: number,
      d: number,
      bar: number,
    ) => {
      const base = v
      const corners: [number, number][] = [
        [-w, -d],
        [w, -d],
        [w, d],
        [-w, d],
      ]
      // Bottom ring (aFrac 0) then top ring (aFrac 1); both share the footprint
      // position so the bar is extruded entirely in the shader by `up`.
      for (let frac = 0; frac <= 1; frac++) {
        for (const [a, b] of corners) {
          positions[v * 3 + 0] = c.x + dirA.x * a + dirB.x * b
          positions[v * 3 + 1] = c.y + dirA.y * a + dirB.y * b
          positions[v * 3 + 2] = c.z + dirA.z * a + dirB.z * b
          aUp[v * 3 + 0] = up.x
          aUp[v * 3 + 1] = up.y
          aUp[v * 3 + 2] = up.z
          aFrac[v] = frac
          aBar[v] = bar
          v++
        }
      }
      // bottom 0..3, top 4..7
      const f = [
        0, 2, 1, 0, 3, 2, // bottom
        4, 5, 6, 4, 6, 7, // top
        0, 1, 5, 0, 5, 4,
        1, 2, 6, 1, 6, 5,
        2, 3, 7, 2, 7, 6,
        3, 0, 4, 3, 4, 7,
      ]
      for (const fi of f) indices[ii++] = base + fi
    }

    const Y_UP = new THREE.Vector3(0, 1, 0)
    const X_AXIS = new THREE.Vector3(1, 0, 0)
    const Z_AXIS = new THREE.Vector3(0, 0, 1)
    const d = 0.32 // bar depth (half)

    for (let b = 0; b < bars; b++) {
      const bar = (b + 0.5) / bars

      if (style === 'radial' || style === 'arc') {
        // Bars stand up on a circle (radial = full ring, arc = front 180°).
        const Rc = style === 'arc' ? 5.0 : 4.0
        const span = style === 'arc' ? Math.PI : Math.PI * 2
        // arc: center the opening toward the camera (front, -Z), symmetric.
        const theta = style === 'arc' ? (bar - 0.5) * span : bar * span
        const sin = Math.sin(theta)
        const cos = Math.cos(theta)
        // arc points to the front (z negative); radial wraps the full circle.
        const c =
          style === 'arc'
            ? new THREE.Vector3(sin * Rc, 0, -cos * Rc)
            : new THREE.Vector3(cos * Rc, 0, sin * Rc)
        const tangent =
          style === 'arc'
            ? new THREE.Vector3(cos, 0, sin) // d/dθ of (sin,0,-cos)
            : new THREE.Vector3(-sin, 0, cos)
        const radial =
          style === 'arc'
            ? new THREE.Vector3(sin, 0, -cos)
            : new THREE.Vector3(cos, 0, sin)
        const w = ((span * Rc) / bars) * (style === 'arc' ? 0.42 : 0.7)
        pushBox(c, tangent, radial, Y_UP, w, d, bar)
        continue
      }

      // linear / mirror: a straight, upright row facing the camera.
      const halfWidth = 4.5
      const spacing = (halfWidth * 2) / bars
      const cx = (b - (bars - 1) / 2) * spacing
      const w = spacing * 0.42
      const c = new THREE.Vector3(cx, 0, 0)
      if (style === 'mirror') {
        // Two shorter bars so the mirrored pair stays within view.
        pushBox(c, X_AXIS, Z_AXIS, new THREE.Vector3(0, 0.62, 0), w, d, bar)
        pushBox(c, X_AXIS, Z_AXIS, new THREE.Vector3(0, -0.62, 0), w, d, bar)
      } else {
        pushBox(c, X_AXIS, Z_AXIS, Y_UP, w, d, bar)
      }
    }

    const geo = this.object3D.geometry
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('aUp', new THREE.BufferAttribute(aUp, 3))
    geo.setAttribute('aFrac', new THREE.BufferAttribute(aFrac, 1))
    geo.setAttribute('aBar', new THREE.BufferAttribute(aBar, 1))
    geo.setIndex(new THREE.BufferAttribute(indices, 1))
    geo.computeBoundingSphere()
  }
}
